import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { configApi } from '../../services/api';
import { PageHeader, Spinner } from '../../components/shared/index';
import toast from 'react-hot-toast';

const ROLES = ['manager','project_manager','developer','designer','qa','sales','cs'];

export default function Settings() {
  const qc = useQueryClient();
  const { data: config, isLoading } = useQuery({ queryKey: ['tenant-config'], queryFn: () => configApi.get().then(r => r.data) });
  const [tab, setTab] = useState('weights');

  const updateWeightsMut = useMutation({ mutationFn: configApi.updateWeights, onSuccess: () => { qc.invalidateQueries(['tenant-config']); toast.success('Weights saved'); } });
  const updateDistMut    = useMutation({ mutationFn: configApi.updateDistribution, onSuccess: () => { qc.invalidateQueries(['tenant-config']); toast.success('Distribution saved'); } });
  const updateMut        = useMutation({ mutationFn: configApi.update, onSuccess: () => { qc.invalidateQueries(['tenant-config']); toast.success('Settings saved'); } });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  const weights = config?.scoring_weights || {};
  const dist    = config?.distribution    || {};

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <PageHeader title="Settings" subtitle="Configure scoring, distribution, and review rules" />
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {[['weights','Scoring weights'],['distribution','Band distribution'],['general','General']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab===k?'border-blue-600 text-blue-600':'border-transparent text-gray-500 hover:text-gray-700'}`}>{l}</button>
        ))}
      </div>

      {tab === 'weights' && (
        <div className="card p-6">
          <p className="text-sm text-gray-500 mb-5">IP + TP + CP must equal 100 for each role. Changes take effect in the next scoring run and are version-tracked.</p>
          <form onSubmit={e => { e.preventDefault(); updateWeightsMut.mutate({ scoring_weights: Object.fromEntries(ROLES.map(r => [r, { ip: parseInt(e.target[`${r}_ip`].value), tp: parseInt(e.target[`${r}_tp`].value), cp: parseInt(e.target[`${r}_cp`].value) }])) }); }}>
            <div className="overflow-x-auto">
              <table className="table">
                <thead><tr>{['Role','IP %','TP %','CP %','Sum'].map(h=><th key={h} className="th">{h}</th>)}</tr></thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {ROLES.map(role => {
                    const w = weights[role] || { ip:70, tp:20, cp:10 };
                    return (
                      <tr key={role}>
                        <td className="td font-medium capitalize">{role.replace('_',' ')}</td>
                        {['ip','tp','cp'].map(k => (
                          <td key={k} className="td"><input type="number" name={`${role}_${k}`} defaultValue={w[k]} min="0" max="100" className="input w-20 text-center" /></td>
                        ))}
                        <td className="td text-gray-400 text-xs">{w.ip+w.tp+w.cp}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end mt-4"><button type="submit" className="btn-primary" disabled={updateWeightsMut.isPending}>Save weights</button></div>
          </form>
        </div>
      )}

      {tab === 'distribution' && (
        <div className="card p-6">
          <p className="text-sm text-gray-500 mb-5">Band allocations by company growth tier. Each tier must sum to 100%.</p>
          <div className="space-y-6">
            {['high_growth','moderate','flat','contraction'].map(tier => {
              const d = dist[tier] || { X:5, 'E+':15, E:65, 'E-':15 };
              return (
                <div key={tier}>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 capitalize">{tier.replace('_',' ')} growth tier</h3>
                  <div className="flex gap-4 flex-wrap">
                    {['X','E+','E','E-'].map(band => (
                      <div key={band} className="flex items-center gap-2">
                        <label className="text-xs text-gray-500">{band}</label>
                        <input type="number" defaultValue={d[band]} min="0" max="100" id={`${tier}_${band.replace('+','p').replace('-','m')}`} className="input w-20 text-center text-sm" />
                        <span className="text-xs text-gray-400">%</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end mt-6">
            <button onClick={() => {
              const newDist = {};
              ['high_growth','moderate','flat','contraction'].forEach(t => {
                newDist[t] = { X: +document.getElementById(`${t}_X`)?.value, 'E+': +document.getElementById(`${t}_Ep`)?.value, E: +document.getElementById(`${t}_E`)?.value, 'E-': +document.getElementById(`${t}_Em`)?.value };
              });
              updateDistMut.mutate({ distribution: newDist });
            }} className="btn-primary" disabled={updateDistMut.isPending}>Save distribution</button>
          </div>
        </div>
      )}

      {tab === 'general' && (
        <div className="card p-6 space-y-5">
          <div className="grid grid-cols-2 gap-5">
            <div><label className="label">Small team threshold (employees)</label><input type="number" className="input" defaultValue={config?.small_team_threshold||15} id="stt" /><p className="text-xs text-gray-400 mt-1">Teams under this size skip forced distribution</p></div>
            <div><label className="label">PIP duration (days)</label><input type="number" className="input" defaultValue={config?.pip_duration_days||90} id="pipdur" /></div>
            <div><label className="label">Peer review minimum count</label><input type="number" className="input" defaultValue={config?.peer_review_min_count||3} id="peermin" /></div>
            <div className="flex items-start gap-3 pt-6"><input type="checkbox" id="forceDistSmall" defaultChecked={config?.force_distribution_small_teams} className="mt-1" /><label htmlFor="forceDistSmall" className="text-sm text-gray-700">Force distribution on small teams</label></div>
          </div>
          <div className="flex justify-end">
            <button onClick={() => updateMut.mutate({ small_team_threshold: +document.getElementById('stt')?.value, pip_duration_days: +document.getElementById('pipdur')?.value, peer_review_min_count: +document.getElementById('peermin')?.value, force_distribution_small_teams: document.getElementById('forceDistSmall')?.checked })} className="btn-primary" disabled={updateMut.isPending}>Save settings</button>
          </div>
        </div>
      )}
    </div>
  );
}
